import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Button, Card, Field, Input, PageHeader, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime } from '../../lib/format';

type Settings = components['schemas']['PlatformSettings'];
type SettingsUpdate = components['schemas']['PlatformSettingsUpdate'];

export function SettingsPage() {
  const query = useQuery({ queryKey: ['settings'], queryFn: () => unwrap(api.GET('/settings')) });
  return (
    <>
      <PageHeader
        title="Setări & securitate"
        subtitle="Politicile globale ale platformei. Salvarea cere confirmare suplimentară (cod TOTP)."
      />
      {query.isLoading && <p className="muted">Se încarcă…</p>}
      {query.error && <p style={{ color: 'var(--red)' }}>{errorMessage(query.error)}</p>}
      {query.data && <SettingsForm key={query.data.updatedAt} settings={query.data} />}
    </>
  );
}

/** Formularul e remontat la fiecare versiune a setărilor (cheia `updatedAt`), fără efecte de reset. */
function SettingsForm({ settings }: { settings: Settings }) {
  const { withStepUp } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const form = useForm<SettingsUpdate>({ defaultValues: pick(settings) });
  const [testTo, setTestTo] = useState('');
  const [smsStatus, setSmsStatus] = useState<components['schemas']['SmsStatus'] | null>(null);

  const save = useMutation({
    mutationFn: (d: SettingsUpdate) => {
      // Politicile numerice pleacă mereu; câmpurile de canale doar dacă s-au schimbat; secretele doar dacă au fost completate.
      const initial = pick(settings);
      const changed = (k: keyof SettingsUpdate) => String(d[k] ?? '') !== String(initial[k] ?? '');
      const body: SettingsUpdate = {
        keyLength: Number(d.keyLength) as 6 | 8,
        defaultKeyHours: Number(d.defaultKeyHours),
        remoteOpenTtlMin: Number(d.remoteOpenTtlMin),
        maxCodeAttempts: Number(d.maxCodeAttempts),
        attemptsWindowMin: Number(d.attemptsWindowMin),
        documentMaxMb: Number(d.documentMaxMb),
      };
      if (changed('emailEnabled')) body.emailEnabled = !!d.emailEnabled;
      if (changed('smsEnabled')) body.smsEnabled = !!d.smsEnabled;
      if (changed('notifyFromName')) body.notifyFromName = d.notifyFromName;
      if (changed('smtpHost')) body.smtpHost = d.smtpHost || null;
      if (changed('smtpPort')) body.smtpPort = Number(d.smtpPort);
      if (changed('smtpSecure')) body.smtpSecure = !!d.smtpSecure;
      if (changed('smtpUser')) body.smtpUser = d.smtpUser || null;
      if (changed('smtpFrom')) body.smtpFrom = d.smtpFrom || null;
      if (d.smtpPassword) body.smtpPassword = d.smtpPassword;
      if (changed('smsoSender')) body.smsoSender = d.smsoSender || null;
      if (d.smsoApiKey) body.smsoApiKey = d.smsoApiKey;
      return withStepUp((headers) =>
        unwrap(api.PUT('/settings', { params: { header: headers }, body })),
      );
    },
    onSuccess: (s) => {
      toast('Setările au fost salvate.', 'success');
      qc.setQueryData(['settings'], s);
      void qc.invalidateQueries({ queryKey: ['settings', 'channels'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const testEmail = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/settings/email/test', { params: { header: headers }, body: { to: testTo } }),
        ),
      ),
    onSuccess: (d) =>
      toast(
        d.status === 'sent'
          ? 'E-mailul de test a fost trimis.'
          : `Livrarea are starea „${d.status}”: ${d.lastError ?? 'vezi Livrări'}`,
        d.status === 'sent' ? 'success' : 'error',
      ),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const checkSms = useMutation({
    mutationFn: () => unwrap(api.GET('/settings/sms/status')),
    onSuccess: (s) => setSmsStatus(s),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const errors = form.formState.errors;
  const smsEnabled = useWatch({ control: form.control, name: 'smsEnabled' });

  return (
    <form
      className="stack"
      onSubmit={form.handleSubmit((d) => save.mutate(d))}
      style={{ maxWidth: 760 }}
    >
      <Card>
        <h2>Politici de securitate</h2>
        <div className="stack" style={{ marginTop: 12 }}>
          <Field
            label="Lungimea codurilor de acces"
            hint="Se aplică cheilor emise de acum înainte."
            htmlFor="set-keylen"
          >
            <Select id="set-keylen" {...form.register('keyLength')}>
              <option value={6}>6 cifre</option>
              <option value={8}>8 cifre</option>
            </Select>
          </Field>
          <Field
            label="Valabilitatea implicită a cheilor (ore)"
            hint="1–168"
            error={errors.defaultKeyHours?.message}
            htmlFor="set-hours"
          >
            <Input
              id="set-hours"
              type="number"
              min={1}
              max={168}
              invalid={!!errors.defaultKeyHours}
              {...form.register('defaultKeyHours', {
                required: true,
                min: { value: 1, message: 'Minimum 1' },
                max: { value: 168, message: 'Maximum 168' },
              })}
            />
          </Field>
          <Field
            label="Expirarea cererilor de deschidere administrativă (minute)"
            hint="5–60"
            error={errors.remoteOpenTtlMin?.message}
            htmlFor="set-ttl"
          >
            <Input
              id="set-ttl"
              type="number"
              min={5}
              max={60}
              invalid={!!errors.remoteOpenTtlMin}
              {...form.register('remoteOpenTtlMin', {
                required: true,
                min: { value: 5, message: 'Minimum 5' },
                max: { value: 60, message: 'Maximum 60' },
              })}
            />
          </Field>
          <Field
            label="Coduri greșite permise la kiosk"
            hint="3–10, în fereastra de mai jos"
            error={errors.maxCodeAttempts?.message}
            htmlFor="set-attempts"
          >
            <Input
              id="set-attempts"
              type="number"
              min={3}
              max={10}
              invalid={!!errors.maxCodeAttempts}
              {...form.register('maxCodeAttempts', {
                required: true,
                min: { value: 3, message: 'Minimum 3' },
                max: { value: 10, message: 'Maximum 10' },
              })}
            />
          </Field>
          <Field
            label="Fereastra încercărilor (minute)"
            hint="1–60"
            error={errors.attemptsWindowMin?.message}
            htmlFor="set-window"
          >
            <Input
              id="set-window"
              type="number"
              min={1}
              max={60}
              invalid={!!errors.attemptsWindowMin}
              {...form.register('attemptsWindowMin', {
                required: true,
                min: { value: 1, message: 'Minimum 1' },
                max: { value: 60, message: 'Maximum 60' },
              })}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h2>Documente</h2>
        <p className="muted small">
          Fișierele se încarcă direct în stocarea S3 / MinIO prin URL-uri presemnate și sunt
          verificate la confirmare (dimensiune, tip după conținut, hash). Tipuri permise: PDF, DOCX,
          PNG, JPG, TXT. Retenția se stabilește per organizație (Organizații & locații), implicit 90
          de zile.
        </p>
        <div className="stack" style={{ marginTop: 12 }}>
          <Field
            label="Dimensiunea maximă a unui document (MB)"
            hint="1–100"
            error={errors.documentMaxMb?.message}
            htmlFor="set-docmax"
          >
            <Input
              id="set-docmax"
              type="number"
              min={1}
              max={100}
              invalid={!!errors.documentMaxMb}
              {...form.register('documentMaxMb', {
                required: true,
                min: { value: 1, message: 'Minimum 1' },
                max: { value: 100, message: 'Maximum 100' },
              })}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h2>Canale de notificare</h2>
        <p className="muted small">
          Implicit se trimite doar e-mail. SMS-urile pleacă numai dacă administratorul platformei
          activează opțiunea de mai jos (D13); cu SMS dezactivat, livrările SMS apar în jurnal ca
          „omise”.
        </p>
        <div className="stack" style={{ marginTop: 12 }}>
          <label className="flex">
            <input type="checkbox" {...form.register('emailEnabled')} /> E-mail activ
          </label>
          <label className="flex">
            <input type="checkbox" {...form.register('smsEnabled')} /> SMS activ (SMSO)
          </label>
          <Field
            label="Numele expeditorului"
            hint="Apare în mesaje și în subiect"
            htmlFor="set-from-name"
          >
            <Input id="set-from-name" maxLength={80} {...form.register('notifyFromName')} />
          </Field>
        </div>

        <h3 style={{ marginTop: 20 }}>
          Server SMTP{' '}
          {settings.smtpConfigured ? (
            <Badge tone="green">configurat</Badge>
          ) : (
            <Badge tone="neutral">neconfigurat</Badge>
          )}
        </h3>
        <p className="muted small">
          Fără server configurat, backend-ul folosește `SMTP_URL` din mediu (Mailpit în dezvoltare).
        </p>
        <div className="stack" style={{ marginTop: 10 }}>
          <div className="flex wrap">
            <Field label="Host" htmlFor="set-smtp-host">
              <Input
                id="set-smtp-host"
                placeholder="smtp.example.com"
                {...form.register('smtpHost')}
              />
            </Field>
            <Field label="Port" htmlFor="set-smtp-port">
              <Input
                id="set-smtp-port"
                type="number"
                min={1}
                max={65535}
                style={{ width: 110 }}
                {...form.register('smtpPort')}
              />
            </Field>
            <label className="flex small" style={{ alignSelf: 'flex-end', paddingBottom: 10 }}>
              <input type="checkbox" {...form.register('smtpSecure')} /> TLS implicit (465)
            </label>
          </div>
          <div className="flex wrap">
            <Field label="Utilizator" htmlFor="set-smtp-user">
              <Input id="set-smtp-user" autoComplete="off" {...form.register('smtpUser')} />
            </Field>
            <Field
              label="Parolă"
              hint={
                settings.smtpConfigured
                  ? 'Setată; lasă gol ca să o păstrezi'
                  : 'Nu se afișează niciodată'
              }
              htmlFor="set-smtp-pass"
            >
              <Input
                id="set-smtp-pass"
                type="password"
                autoComplete="new-password"
                placeholder={settings.smtpConfigured ? '••••••••' : ''}
                {...form.register('smtpPassword')}
              />
            </Field>
          </div>
          <Field
            label="Expeditor"
            hint='Ex. "Synaptic Smart Locker" <no-reply@synaptic.ro>'
            htmlFor="set-smtp-from"
          >
            <Input id="set-smtp-from" {...form.register('smtpFrom')} />
          </Field>
          <div className="flex wrap">
            <Input
              placeholder="adresa@exemplu.ro"
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              aria-label="Adresa pentru testul de e-mail"
              style={{ maxWidth: 280 }}
            />
            <Button
              type="button"
              icon="mail"
              onClick={() => testEmail.mutate()}
              busy={testEmail.isPending}
              disabled={!testTo.includes('@')}
            >
              Trimite e-mail de test
            </Button>
          </div>
        </div>

        <h3 style={{ marginTop: 20 }}>
          SMSO{' '}
          {settings.smsoConfigured ? (
            <Badge tone="green">configurat</Badge>
          ) : (
            <Badge tone="neutral">neconfigurat</Badge>
          )}
        </h3>
        <p className="muted small">
          Cheia API vine din contul SMSO (app.smso.ro → Developers); expeditorul este id-ul numeric
          al unui sender aprobat.
          {!smsEnabled &&
            ' SMS-urile sunt dezactivate global: configurația se păstrează, dar nu se trimite nimic.'}
        </p>
        <div className="stack" style={{ marginTop: 10 }}>
          <div className="flex wrap">
            <Field
              label="Cheie API"
              hint={settings.smsoConfigured ? 'Setată; lasă gol ca să o păstrezi' : 'Write-only'}
              htmlFor="set-smso-key"
            >
              <Input
                id="set-smso-key"
                type="password"
                autoComplete="new-password"
                placeholder={settings.smsoConfigured ? '••••••••' : ''}
                {...form.register('smsoApiKey')}
              />
            </Field>
            <Field label="Expeditor (sender id)" htmlFor="set-smso-sender">
              <Input id="set-smso-sender" style={{ width: 160 }} {...form.register('smsoSender')} />
            </Field>
            <Button
              type="button"
              onClick={() => checkSms.mutate()}
              busy={checkSms.isPending}
              style={{ alignSelf: 'flex-end' }}
              disabled={!settings.smsoConfigured}
            >
              Verifică SMSO
            </Button>
          </div>
          {smsStatus && (
            <p className="small">
              {smsStatus.reachable ? (
                <>
                  <Badge tone="green">conectat</Badge> credit {smsStatus.credit} · expeditori:{' '}
                  {smsStatus.senders.map((x) => `${x.name} (#${x.id})`).join(', ') || '—'}
                </>
              ) : (
                <>
                  <Badge tone="red">indisponibil</Badge>{' '}
                  {smsStatus.error ?? (smsStatus.configured ? '' : 'neconfigurat')}
                </>
              )}
            </p>
          )}
        </div>
      </Card>

      <div className="flex between">
        <span className="muted small">Ultima modificare: {fmtDateTime(settings.updatedAt)}</span>
        <Button variant="primary" type="submit" busy={save.isPending} icon="lock">
          Salvează cu confirmare
        </Button>
      </div>
    </form>
  );
}

function pick(s: Settings): SettingsUpdate {
  return {
    keyLength: s.keyLength,
    defaultKeyHours: s.defaultKeyHours,
    remoteOpenTtlMin: s.remoteOpenTtlMin,
    maxCodeAttempts: s.maxCodeAttempts,
    attemptsWindowMin: s.attemptsWindowMin,
    documentMaxMb: s.documentMaxMb,
    emailEnabled: s.emailEnabled,
    smsEnabled: s.smsEnabled,
    notifyFromName: s.notifyFromName,
    smtpHost: s.smtpHost ?? '',
    smtpPort: s.smtpPort,
    smtpSecure: s.smtpSecure,
    smtpUser: s.smtpUser ?? '',
    smtpFrom: s.smtpFrom ?? '',
    smsoSender: s.smsoSender ?? '',
    smtpPassword: '',
    smsoApiKey: '',
  };
}
